// [at, di, dk, supply, speed]
const unitValues = {
    31: [10, 25, 20, 1, 20],
    32: [20, 35, 40, 1, 20],
    33: [60, 40, 60, 1, 20],
    34: [80, 66, 50, 1, 20],
    35: [50, 70, 33, 2, 20],
    36: [100, 80, 70, 2, 20],
    37: [250, 140, 200, 3, 20],
    38: [450, 380, 240, 3, 20],
    39: [200, 170, 250, 3, 20],
    40: [600, 440, 520, 5, 20],
    64: [120, 30, 15, 2, 16],
};

function strengthByLevelSingle(val, supply, level, allianceBonus) {
    const base = (1.007 ** level) * val / supply;
    const supplyBonus = 0.3 * (1 + (1.007 ** (level - 1) - 1) / (0.007));
    return Math.round(10000 * Math.max(0, (1 + allianceBonus) * supply * (base + supplyBonus))) / 10000;
}

function strengthByLevel([at, di, dk, supply], level = 0, allianceBonus = 0) {
    return [
        strengthByLevelSingle(at, supply, level, allianceBonus),
        strengthByLevelSingle(di, supply, level, allianceBonus),
        strengthByLevelSingle(dk, supply, level, allianceBonus),
    ];
}

/**
 * @param units Unit[]
 * @param allianceBonus
 * @returns {*}
 */
export function calculateStrength(units, allianceBonus = 0) {
    return units
        .map(unit => strengthByLevel(unitValues[unit.unitType], unit.unitLevel ?? 0, allianceBonus).map(k => k * unit.unitAmount))
        .reduce(([at, di, dk], [dat, ddi, ddk]) => [at + dat, di + ddi, dk + ddk], [0, 0, 0]);
}

export function countUnits(units) {
    return units.map(unit => unit.unitAmount).reduce((a, b) => a + b, 0);
}

export function simpleSimulate(attackerUnits, defenderUnits) {
    const attackerStrength = calculateStrength(attackerUnits, 0.04)[0],
        defenderStrength = calculateStrength(defenderUnits)[2] + 10;

    if (attackerStrength === 0) {
        return 1;
    }

    const overpower = Math.round(10000 * ((countUnits(attackerUnits) + countUnits(defenderUnits)) ** 0.015 - 1.1092)) / 10000;
    const overpowerBonus = 0.75 - Math.max(0, Math.min(0.1211, overpower));
    const overpoweredRatio = (defenderStrength / attackerStrength) ** overpowerBonus;
    return overpoweredRatio / (overpoweredRatio + 1 / overpoweredRatio);
}

function splitTroops(units, percentage, ceil = false) {
    const func = ceil ? Math.ceil : Math.round;
    return units.map(unit => ({ ...unit, unitAmount: unit.unitAmount - func(unit.unitAmount * (1 - percentage)) }))
}

function calculateAnimalBounty(units) {
    return units.map(unit => unit.unitAmount * unitValues[unit.unitType][3] * 40).reduce((a, b) => a + b, 0);
}

export function calculateOasisBattle(attackerUnits, defenderUnits, hospital = true) {
    const attackerLossesPercent = simpleSimulate(attackerUnits, defenderUnits);
    const attackerLosses = splitTroops(attackerUnits, attackerLossesPercent);
    const attackerWounded = splitTroops(attackerLosses, hospital ? 0.4 : 0.0, true);
    const defenderLosses = splitTroops(defenderUnits, 1 - attackerLossesPercent);

    const animalBounty = calculateAnimalBounty(defenderLosses);

    return {
        attackerUnits,
        attackerLosses,
        attackerWounded,
        defenderUnits,
        defenderLosses,
        animalBounty,
    };
}

export function findTroopThresholds(attackerUnit, defenderUnits, ignoreWounded = false, forWhich = null) {
    // We want to kill at least this many animals.
    const minimumKillRatio = 0.97;

    function losses(unitCount, ignoreWounded) {
        const battleResult = calculateOasisBattle([{
            ...attackerUnit,
            unitAmount: unitCount
        }], defenderUnits);

        return battleResult.attackerLosses[0].unitAmount - (ignoreWounded ? battleResult.attackerWounded[0].unitAmount : 0);
    }

    function seek(desiredLosses) {
        let min = 1, max = 100000, mid = maximumUnits * 2;
        let steps = 0, anyValidAnswer = false;
        do {
            if (losses(mid, ignoreWounded) > desiredLosses || losses(mid, false) >= mid * (1 - minimumKillRatio)) {
                min = mid + 1;
                mid = Math.ceil((max + min) / 2);
            } else {
                max = mid;
                mid = Math.floor((max + min) / 2);
                anyValidAnswer = true;
            }
        } while ((max - min) > 0 && ++steps < 100);
        return anyValidAnswer ? Math.max(desiredLosses, mid) : null;
    }

    const maximumUnits = attackerUnit.unitAmount;

    if (forWhich === null) {
        return {
            0: seek(0),
            1: seek(1),
            2: seek(2),
            3: seek(3),
            'l200': seek(losses(200)),
            'l800': seek(losses(800)),
            'full': seek(losses(maximumUnits)),
        }
    } else {
        const result = {};
        for (const count of forWhich) {
            const myCount = seek(count);
            result[count] = { units: myCount, losses: losses(myCount) };
        }
        return result;
    }
}

export function calculateOasisProduction(bonusTypes, serverSpeed) {
    const production = [10, 10, 10, 10];
    bonusTypes.forEach(bonus => {
        production[bonus.bonusType - 1] = 40 * bonus.bonusTier;
    });
    return production.map(k => k * serverSpeed);
}
